import { useTranslation } from '../../i18n';
import { currencySymbol, formatMoney, PLATFORM_CURRENCY } from '../../utils/money';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input, InputNumber, Modal, Popover, Radio, Space, Switch, Tag, Tooltip, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ok } from './vmdetail/utils';
import { userPath } from '../../config/paths';

const { Text } = Typography;

/** Days until an expiry timestamp ("YYYY-MM-DD HH:mm:ss"), or null. */
export const daysLeft = (v) => (v ? Math.ceil((new Date(String(v).replace(' ', 'T')) - Date.now()) / 86400000) : null);

/** Period picker → renewal invoice, paid from balance when it covers it. */
export const RenewModal = ({ vmId, open, onClose, onDone, api }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [months, setMonths] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setInfo(null);
        api.getVmBilling(vmId).then((res) => {
            const d = ok(res) ? res.data : { periods: [] };
            setInfo(d);
            const cur = (d.periods || []).find((p) => p.months === d.cycle_months);
            setMonths((cur || (d.periods || [])[0])?.months ?? null);
        });
    }, [open, vmId, api]);

    const submit = async () => {
        setSubmitting(true);
        const res = await api.renewVm(vmId, months);
        setSubmitting(false);
        if (!ok(res)) {
            message.error(res?.msg || res?.error || t('Renewal failed'));
            return;
        }
        if (res.data?.paid) {
            message.success(t('Renewed'));
        } else {
            message.warning(t('Insufficient balance. Please pay the invoice.'));
            navigate(userPath(`/billing/invoices/${res.data?.invoice_id}`));
        }
        onClose();
        onDone && onDone();
    };

    const periods = info?.periods || [];
    const amount = periods.find((p) => p.months === months)?.amount;
    return (
        <Modal
            title={t('Renew')}
            open={open}
            onCancel={onClose}
            onOk={submit}
            okText={amount != null ? `${t('Renew')} ${formatMoney(amount, info?.currency || PLATFORM_CURRENCY)}` : t('Renew')}
            okButtonProps={{ disabled: !months, loading: submitting }}
            confirmLoading={submitting}
            destroyOnClose
        >
            {info && !periods.length ? (
                <Text type="secondary">{t('Renewal not available yet')}</Text>
            ) : (
                <Radio.Group value={months} onChange={(e) => setMonths(e.target.value)}>
                    <Space direction="vertical">
                        {periods.map((p) => (
                            <Radio key={p.months} value={p.months}>
                                {t('{{n}} months', { n: p.months })} <Text type="secondary">{formatMoney(p.amount, p.currency || info?.currency || PLATFORM_CURRENCY)}</Text>
                            </Radio>
                        ))}
                    </Space>
                </Radio.Group>
            )}
        </Modal>
    );
};

/** 释放实例 (delete now): confirm by typing the name. Hourly refunds unused hours; monthly does not. */
const ReleaseButton = ({ vmId, api, name, hourly, size }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState('');
    const [busy, setBusy] = useState(false);
    if (!name || typeof api.releaseVm !== 'function') return null;
    const release = async () => {
        setBusy(true);
        const res = await api.releaseVm(vmId, typed.trim());
        setBusy(false);
        if (ok(res)) { message.success(t('Released')); setOpen(false); navigate(userPath('/evm/vms')); } else message.error(res?.msg || res?.error || t('Operation failed'));
    };
    return (
        <>
            <Button size={size} danger onClick={() => { setTyped(''); setOpen(true); }}>{t('Release')}</Button>
            <Modal
                title={t('Release instance')}
                open={open}
                onCancel={() => setOpen(false)}
                onOk={release}
                okText={t('Release')}
                okButtonProps={{ danger: true, disabled: typed.trim() !== String(name), loading: busy }}
                destroyOnClose
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>{hourly ? t('The instance and its data are deleted right away and cannot be recovered. Unused whole hours go back to your balance.') : t('The instance and its data are deleted right away and cannot be recovered. The remaining prepaid time is not refunded.')}</Text>
                    <Text type="secondary">{t('Type the instance name {{name}} to confirm', { name })}</Text>
                    <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={name} />
                </Space>
            </Modal>
        </>
    );
};

/** Auto-renew switch + 续费 button for one VM (detail page). Hidden when the VM has no subscription. */
export const VmRenewInline = ({ vmId, api, size = 'small' }) => {
    const { t } = useTranslation();
    const [info, setInfo] = useState(null);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const [capOpen, setCapOpen] = useState(false);

    const [capVal, setCapVal] = useState(null);
    const saveCap = async (v) => {
        setSaving(true);
        const res = await api.setVmTrafficCap(vmId, v);
        setSaving(false);
        if (ok(res)) { message.success(t('Saved')); setCapOpen(false); load(); } else message.error(res?.msg || res?.error || t('Failed to save'));
    };

    const load = useCallback(() => {
        api.getVmBilling(vmId).then((res) => setInfo(ok(res) ? res.data : null));
    }, [api, vmId]);
    useEffect(() => { load(); }, [load]);

    if (!info?.subscribed) return null;
    const payg = info.payg;
    if (payg) {
        // 按小时：自动从余额预扣，没有续费/自动续费
        const cur = payg.currency || PLATFORM_CURRENCY;
        const tr = payg.traffic || {};
        return (
            <Space size={12} wrap>
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>{t('Hourly')}</Tag>
                <Text>{formatMoney(payg.rate, cur, { decimals: 4 })}{t('/hr')}</Text>
                {payg.paid_until && <Text type="secondary">{t('Paid until {{at}}', { at: String(payg.paid_until).slice(5, 16) })}</Text>}
                {tr.per_gb > 0 && (
                    <Tooltip title={tr.cap > 0 ? t('At most {{cap}} a month; throttled until the reset after that', { cap: formatMoney(tr.cap, cur) }) : null}>
                        <Text type="secondary">{t('Public outbound traffic')} {tr.out_gb} GB × {formatMoney(tr.per_gb, cur, { decimals: 4 })}</Text>
                    </Tooltip>
                )}
                {tr.per_gb > 0 && (
                    <Popover
                        trigger="click"
                        open={capOpen}
                        onOpenChange={(o) => { setCapOpen(o); if (o) setCapVal(tr.user_cap ?? tr.cap ?? null); }}
                        content={(
                            <Space direction="vertical" size={8}>
                                <InputNumber min={tr.min_user_cap || 1} max={tr.platform_cap > 0 ? tr.platform_cap : undefined} step={1} value={capVal}
                                    onChange={setCapVal} addonBefore={currencySymbol(cur)} style={{ width: 160 }} />
                                <Space>
                                    <Button size="small" type="primary" loading={saving} disabled={!capVal} onClick={() => saveCap(capVal)}>{t('Save')}</Button>
                                    {tr.user_cap != null && <Button size="small" loading={saving} onClick={() => saveCap(null)}>{t('Restore defaults')}</Button>}
                                </Space>
                            </Space>
                        )}
                        title={t('Monthly traffic-fee cap')}
                    >
                        <Button size={size} type="link" style={{ padding: 0 }}>{t('Cap')} {tr.cap > 0 ? formatMoney(tr.cap, cur) : t('Unlimited')}</Button>
                    </Popover>
                )}
                <ReleaseButton vmId={vmId} api={api} name={payg.name} hourly size={size} />
                {payg.suspended_for_balance && (
                    <Space size={6}>
                        <Tag color="red" style={{ marginInlineEnd: 0 }}>{t('Stopped: low balance')}</Tag>
                        <Button size={size} type="primary" onClick={() => navigate(userPath('/billing/balance'))}>{t('Top up')}</Button>
                    </Space>
                )}
            </Space>
        );
    }
    const toggle = async (v) => {
        setSaving(true);
        const res = await api.setVmAutoRenew(vmId, v);
        setSaving(false);
        if (ok(res)) setInfo({ ...info, auto_renew: v });
        else message.error(res?.msg || res?.error || t('Operation failed'));
    };
    return (
        <Space size={12} wrap>
            <Tooltip title={t('Auto-renew from balance before expiry')}>
                <Space size={6}>
                    <Switch size={size} checked={!!info.auto_renew} loading={saving} onChange={toggle} />
                    <Text>{t('Auto-renewal')}</Text>
                </Space>
            </Tooltip>
            <Button size={size} type="primary" ghost onClick={() => setOpen(true)}>{t('Renew')}</Button>
            <ReleaseButton vmId={vmId} api={api} name={info.name} hourly={false} size={size} />
            <RenewModal vmId={vmId} api={api} open={open} onClose={() => setOpen(false)} onDone={load} />
        </Space>
    );
};
